/**
 * Narration « business » du rapport : transforme les scores en langage client.
 *
 * Partagé par l’export JSON rapport client et la synthèse projet.
 * Aucune estimation chiffrée trompeuse : la projection est une fourchette indicative.
 */

const PILLAR_LABEL = {
  seo: 'le référencement (SEO)',
  performance: 'la vitesse de chargement',
  technique: 'la santé technique',
};

/** Question client + message d'impact si le pilier est faible. */
const PILLAR_IMPACT = {
  seo: {
    question: 'Google comprend-il et met-il en avant vos pages ?',
    low: 'Un référencement insuffisant limite directement votre visibilité gratuite sur Google.',
    ok: 'Vos pages sont correctement comprises par Google.',
  },
  performance: {
    question: 'Votre site est-il rapide, surtout sur mobile ?',
    low: 'Une page lente fait fuir une partie des visiteurs avant même qu’ils voient votre offre.',
    ok: 'Votre site offre une vitesse de chargement satisfaisante.',
  },
  technique: {
    question: 'Votre site est-il sain pour l’indexation ?',
    low: 'Des problèmes techniques empêchent certaines pages d’être bien indexées par Google.',
    ok: 'Les fondations techniques de votre site sont saines.',
  },
};

import { getScoreQualitative } from './scoreLabels';

const OK_THRESHOLD = 60;
const WEAK_THRESHOLD = 50;

export function globalTier(score) {
  const q = getScoreQualitative(score);
  if (q.tone === 'neutre') return { label: 'Non évalué', tone: 'neutre' };
  return { label: q.label, tone: q.tone };
}

/** Identifie le pilier le plus faible (score > 0). */
function weakestPillar(pillars) {
  const entries = Object.entries(pillars).filter(([, v]) => Number(v) > 0);
  if (entries.length === 0) return null;
  entries.sort((a, b) => a[1] - b[1]);
  return entries[0][0];
}

/**
 * Verdict global en langage client.
 * @param {number} scoreGlobal
 * @param {{seo:number, performance:number, technique:number}} pillars
 */
export function verdictGlobal(scoreGlobal, pillars) {
  const tier = globalTier(scoreGlobal);
  if (tier.tone === 'neutre') {
    return 'Le site n’a pas encore été suffisamment audité pour établir un verdict global.';
  }
  const weak = weakestPillar(pillars);
  const weakScore = weak ? Number(pillars[weak]) : 0;

  if (tier.tone === 'positif') {
    if (weak && weakScore < OK_THRESHOLD) {
      return `Votre site est en bonne santé globale. Le principal levier d’amélioration reste ${PILLAR_LABEL[weak]}.`;
    }
    return 'Votre site est en bonne santé sur l’ensemble des critères analysés. L’enjeu est désormais de maintenir ce niveau.';
  }
  if (tier.tone === 'correct') {
    return weak
      ? `Votre site repose sur de bonnes bases, mais ${PILLAR_LABEL[weak]} freine aujourd’hui ses résultats. Quelques priorités bien ciblées le feraient nettement progresser.`
      : 'Votre site repose sur de bonnes bases, avec plusieurs optimisations à portée de main.';
  }
  if (tier.tone === 'moyen') {
    return weak
      ? `Votre site est globalement correct, avec une marge de progression notable sur ${PILLAR_LABEL[weak]}.`
      : 'Votre site est globalement correct, avec une marge de progression sur plusieurs axes.';
  }
  // faible / critique
  return weak
    ? `Votre site présente des points de blocage importants, en particulier sur ${PILLAR_LABEL[weak]}. Un plan d’action prioritaire est recommandé pour rétablir de bons résultats.`
    : 'Votre site présente plusieurs points de blocage importants nécessitant un plan d’action prioritaire.';
}

/**
 * Phrases d'impact par pilier (pour la section scores).
 * @returns {Array<{key, label, score, question, message, weak}>}
 */
export function impactByPillar(pillars) {
  return ['seo', 'performance', 'technique'].map((key) => {
    const score = Number(pillars[key]) || 0;
    const cfg = PILLAR_IMPACT[key];
    const weak = score > 0 && score < WEAK_THRESHOLD;
    return {
      key,
      label: PILLAR_LABEL[key],
      score,
      question: cfg.question,
      message: score === 0 ? 'Pilier non mesuré sur cet audit.' : weak ? cfg.low : cfg.ok,
      weak,
    };
  });
}

/**
 * Projection indicative du score global si les piliers faibles sont remontés
 * au seuil « bon ». Retourne une fourchette honnête (jamais une promesse ferme).
 * @returns {{min:number, max:number, gain:number} | null}
 */
export function projectedScore(pillars) {
  const present = Object.entries(pillars).filter(([, v]) => Number(v) > 0);
  if (present.length === 0) return null;

  const current = present.reduce((s, [, v]) => s + Number(v), 0) / present.length;
  const improved = present.map(([, v]) => {
    const n = Number(v);
    return n < WEAK_THRESHOLD ? Math.min(WEAK_THRESHOLD + 12, 78) : Math.min(n + 6, 95);
  });
  const projected = improved.reduce((s, v) => s + v, 0) / improved.length;

  const gain = Math.max(0, Math.round(projected - current));
  if (gain < 3) return null; // pas de projection si gain négligeable

  return {
    min: Math.round(projected - 3),
    max: Math.round(projected + 2),
    gain,
  };
}
